import { useEffect, useMemo, useState } from 'react';
import { Plus, ArrowRight, Archive } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button.tsx';
import { useOverview, useProducts } from '../../lib/hooks.ts';
import type { Product, ProductOverview } from '../../lib/types.ts';
import { CreateProductDialog } from './CreateProductDialog.tsx';
import { cn } from '../../lib/utils.ts';

type WindowKey = '7' | '30' | '90';

const WINDOW_OPTIONS: Array<{ key: WindowKey; label: string }> = [
  { key: '7', label: '7 дней' },
  { key: '30', label: '30 дней' },
  { key: '90', label: '90 дней' },
];

const ARCHIVED_TOGGLE_STORAGE_KEY = 'overview.show_archived';

export function OverviewPage(): JSX.Element {
  const [windowKey, setWindowKey] = useState<WindowKey>('30');
  const [createOpen, setCreateOpen] = useState(false);
  // Состояние тогглера хранится в localStorage, чтобы переключатель не
  // сбрасывался при F5/перезаходе. Боль архивированных в том, что их
  // редко трогают, и каждый раз открывать форму поиска — лишний шаг.
  const [showArchived, setShowArchived] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(ARCHIVED_TOGGLE_STORAGE_KEY) === 'true';
  });
  useEffect(() => {
    window.localStorage.setItem(ARCHIVED_TOGGLE_STORAGE_KEY, showArchived ? 'true' : 'false');
  }, [showArchived]);

  const range = useMemo(() => {
    const days = Number(windowKey);
    const to = new Date();
    const from = new Date(to.getTime() - days * 24 * 3600 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [windowKey]);

  const products = useProducts(showArchived);
  const overview = useOverview(range);

  const overviewByProductId = useMemo(() => {
    const map = new Map<string, ProductOverview>();
    overview.data?.items.forEach((it) => map.set(it.productId, it));
    return map;
  }, [overview.data]);

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Обзор</h1>
          <p className="text-sm text-fg-muted">Конверсии по всем продуктам</p>
        </div>
        <div className="flex items-center gap-2">
          <ArchivedToggle value={showArchived} onChange={setShowArchived} />
          <WindowToggle value={windowKey} onChange={setWindowKey} />
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            Новый продукт
          </Button>
        </div>
      </div>

      {products.isLoading ? (
        <SkeletonGrid />
      ) : products.data?.items.length === 0 ? (
        <EmptyState onCreate={() => setCreateOpen(true)} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.data?.items.map((p) => (
            <ProductCard key={p.id} product={p} stats={overviewByProductId.get(p.id)} />
          ))}
        </div>
      )}

      <CreateProductDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function ArchivedToggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      title={value ? 'Скрыть архивированные' : 'Показать архивированные'}
      aria-pressed={value}
      className={cn(
        'flex items-center gap-1.5 rounded border border-border px-2.5 py-1 text-xs transition-colors',
        value ? 'bg-bg-elevated text-fg' : 'text-fg-muted hover:text-fg',
      )}
    >
      <Archive className="h-3.5 w-3.5" />
      Архив
    </button>
  );
}

function WindowToggle({
  value,
  onChange,
}: {
  value: WindowKey;
  onChange: (v: WindowKey) => void;
}): JSX.Element {
  return (
    <div className="flex rounded border border-border p-0.5">
      {WINDOW_OPTIONS.map((opt) => (
        <button
          key={opt.key}
          onClick={() => onChange(opt.key)}
          className={cn(
            'rounded px-2.5 py-1 text-xs transition-colors',
            value === opt.key ? 'bg-bg-elevated text-fg' : 'text-fg-muted hover:text-fg',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function ProductCard({
  product,
  stats,
}: {
  product: Product;
  stats: ProductOverview | undefined;
}): JSX.Element {
  const isArchived = Boolean(product.archivedAt);
  return (
    <Link
      to={`/products/${product.id}`}
      className={cn('card group flex flex-col', isArchived && 'opacity-60')}
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium">{product.name}</div>
          <div className="truncate font-mono text-xs text-fg-muted">{product.slug}</div>
        </div>
        {isArchived ? (
          <span className="flex flex-shrink-0 items-center gap-1 rounded border border-border bg-bg-elevated px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-fg-muted">
            <Archive className="h-3 w-3" />
            Архив
          </span>
        ) : null}
      </div>

      <div className="mb-4 grid grid-cols-3 gap-2">
        <Stat label="Визиты" value={stats?.visitsTotal ?? 0} sub={stats?.visitsUnique ?? 0} />
        <Stat label="Клики" value={stats?.clicksTotal ?? 0} sub={stats?.clicksUnique ?? 0} />
        <Stat label="Конверсия" value={`${stats?.conversionPct ?? 0}%`} subDimmed />
      </div>

      <div className="mt-auto flex items-center justify-between text-xs text-fg-muted">
        <span>уникальных в подзаголовке</span>
        <span className="flex items-center gap-1 text-accent group-hover:gap-2 transition-all">
          Открыть
          <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  );
}

function Stat({
  label,
  value,
  sub,
  subDimmed,
}: {
  label: string;
  value: string | number;
  sub?: string | number;
  subDimmed?: boolean;
}): JSX.Element {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-fg-muted/80">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      {sub !== undefined && !subDimmed ? (
        <div className="text-xs text-fg-muted tabular-nums">{sub} уник</div>
      ) : null}
    </div>
  );
}

function SkeletonGrid(): JSX.Element {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="card animate-pulse">
          <div className="mb-4 h-5 w-1/2 rounded bg-bg-elevated" />
          <div className="mb-2 h-3 w-1/3 rounded bg-bg-elevated" />
          <div className="mt-6 grid grid-cols-3 gap-2">
            <div className="h-8 rounded bg-bg-elevated" />
            <div className="h-8 rounded bg-bg-elevated" />
            <div className="h-8 rounded bg-bg-elevated" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }): JSX.Element {
  return (
    <div className="card flex flex-col items-center gap-3 py-12 text-center">
      <div className="text-fg-muted">Пока нет продуктов</div>
      <p className="max-w-sm text-sm text-fg-muted">
        Продукт — это отдельный канал или направление. Создай первый, чтобы получить API-токен и
        начать собирать конверсии.
      </p>
      <Button variant="primary" onClick={onCreate}>
        <Plus className="h-4 w-4" />
        Создать первый продукт
      </Button>
    </div>
  );
}
